import { useState } from 'react';
import { useNotifications } from '@/contexts/NotificationContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Bell, CheckCheck, Trash2, AlertCircle, AlertTriangle, Info, CheckCircle,
  Search, FileText, CreditCard, GraduationCap, Award, Settings, Filter
} from 'lucide-react';

const NotificationIcon = ({ type }) => {
  switch (type) {
    case 'error':
    case 'alert':
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    case 'success':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    default:
      return <Info className="h-4 w-4 text-blue-500" />;
  }
};

const CategoryIcon = ({ category }) => {
  switch (category) {
    case 'applications':
      return <FileText className="h-3 w-3" />;
    case 'payments':
      return <CreditCard className="h-3 w-3" />;
    case 'exams':
      return <GraduationCap className="h-3 w-3" />;
    case 'certificates':
      return <Award className="h-3 w-3" />;
    default:
      return <Settings className="h-3 w-3" />;
  }
};

const CATEGORIES = [
  { id: 'all', label: 'All', icon: Bell },
  { id: 'applications', label: 'Applications', icon: FileText },
  { id: 'payments', label: 'Payments', icon: CreditCard },
  { id: 'exams', label: 'Exams', icon: GraduationCap },
  { id: 'certificates', label: 'Certificates', icon: Award },
  { id: 'system', label: 'System', icon: Settings },
];

export default function NotificationBell() {
  const { 
    notifications, 
    unreadCount, 
    isConnected, 
    markAsRead, 
    markAllAsRead, 
    clearAll,
    getNotificationsByCategory,
    getUnreadCountByCategory
  } = useNotifications();
  
  const [open, setOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter notifications by category and search
  const filteredNotifications = getNotificationsByCategory(activeCategory).filter(n => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (n.title || '').toLowerCase().includes(query) ||
      (n.message || '').toLowerCase().includes(query)
    );
  });

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell className={`h-5 w-5 ${isConnected ? 'text-[#1B2A4A]' : 'text-slate-400'}`} />
          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs">
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
          {!isConnected && (
            <span className="absolute bottom-0 right-0 h-2 w-2 bg-slate-400 rounded-full"></span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 max-h-[500px] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="font-semibold text-sm text-[#1B2A4A]">Notifications</span>
          <div className="flex gap-1">
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={markAllAsRead} className="h-7 text-xs">
                <CheckCheck className="h-3 w-3 mr-1" /> Read all
              </Button>
            )}
            {notifications.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearAll} className="h-7 text-xs text-slate-500">
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>

        {/* Category Filter Tabs */}
        <div className="flex items-center gap-1 px-2 py-2 border-b overflow-x-auto">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const count = getUnreadCountByCategory(cat.id);
            return (
              <Button
                key={cat.id}
                variant={activeCategory === cat.id ? "default" : "ghost"}
                size="sm"
                className={`h-7 text-xs flex-shrink-0 ${activeCategory === cat.id ? 'bg-[#1B2A4A]' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setActiveCategory(cat.id);
                }}
              >
                <Icon className="h-3 w-3 mr-1" />
                {cat.label}
                {count > 0 && (
                  <span className={`ml-1 text-xs ${activeCategory === cat.id ? 'text-white' : 'text-red-500'}`}>
                    ({count})
                  </span>
                )}
              </Button>
            );
          })}
        </div>

        {/* Search Bar */}
        <div className="px-2 py-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
            <Input
              type="text"
              placeholder="Search notifications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 pl-7 text-xs"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
        
        {/* Notifications List */}
        <div className="overflow-y-auto flex-1">
          {filteredNotifications.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">
              <Bell className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              <p>No notifications</p>
              <p className="text-xs text-slate-400 mt-1">
                {searchQuery ? 'Try a different search' : isConnected ? "You're all caught up!" : 'Connecting...'}
              </p>
            </div>
          ) : (
            <div>
              {filteredNotifications.slice(0, 15).map((notification) => (
                <DropdownMenuItem
                  key={notification.id}
                  className={`flex items-start gap-3 p-3 cursor-pointer ${!notification.read ? 'bg-blue-50' : ''}`}
                  onClick={() => markAsRead(notification.id)}
                >
                  <NotificationIcon type={notification.type} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-medium truncate ${!notification.read ? 'text-[#1B2A4A]' : 'text-slate-700'}`}>
                        {notification.title}
                      </p>
                      <span className="flex items-center gap-1 text-xs text-slate-400 flex-shrink-0">
                        <CategoryIcon category={notification.category} />
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-2">{notification.message}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(notification.timestamp).toLocaleString()}
                    </p>
                  </div>
                  {!notification.read && (
                    <span className="h-2 w-2 bg-blue-500 rounded-full flex-shrink-0 mt-1"></span>
                  )}
                </DropdownMenuItem>
              ))}
              {filteredNotifications.length > 15 && (
                <div className="p-2 text-center border-t">
                  <p className="text-xs text-slate-500">+{filteredNotifications.length - 15} more notifications</p>
                </div>
              )}
            </div>
          )}
        </div>
        
        <DropdownMenuSeparator />
        <div className="px-3 py-2 text-xs text-slate-400 flex items-center justify-between">
          <div className="flex items-center gap-1">
            <span className={`h-2 w-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-slate-400'}`}></span>
            {isConnected ? 'Real-time updates active' : 'Connecting...'}
          </div>
          <Filter className="h-3 w-3" />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
